import React, {useEffect, useMemo, useState} from "react";
import {css, Theme} from "@emotion/react";

import {Loader} from "@pg-design/loader-module";
import {RequestState} from "@pg-mono/request-state";
import {rpAppLink} from "@pg-mono/rp-routes";

import {ApplicationSourceSection} from "../application/utils/ApplicationSource";
import {useShouldShowPrice} from "../hooks/use_should_show_price";
import {OfferDisplayType} from "../offer/helpers/OfferDisplayType";
import {IMapOfferSidebar} from "../offer/map/types/MapOfferSidebar";
import {IOfferDetail} from "../offer/types/IOfferDetail";
import {ISalesOffice} from "../offer/types/ISalesOffice";
import {OfferPriceType} from "../offer/types/OfferPriceType";
import {OfferType} from "../offer/types/OfferType";
import {getOfferDisplayStatus} from "../offer/utils/get_offer_display_status";
import {PropertyPlanModal} from "../property/components/property_plan_modal/PropertyPlanModal";
import {IPropertyListProperty} from "../property/types/PropertyListProperty";
import {Country} from "../region/types/Country";
import {ShowPropertyPlanView, showPropertyPlanView} from "../tracking/algolytics_hits/click_actions/show_property_plan_click";
import {useGTMEcommercePropertyListViewTracking} from "../tracking/hooks/use_gtm_ecommerce_property_list_view_tracking";
import {PropertyListLoader} from "./PropertyListLoader";
import {PropertyListTableHeader} from "./PropertyListTableHeader";
import {PropertyListTableRow} from "./PropertyListTableRow";

export interface IPropertyListProps {
    offer: IOfferDetail | IMapOfferSidebar;
    properties: IPropertyListProperty[];
    requestState: RequestState;
    sort: string | null;
    onSortChange: (value: string) => void;
    className?: string;
    onPageChange: (value: number) => Promise<unknown[] | void>;
    pageCount: number;
    pageIsLoading: boolean;
    currentPage: number;
    sourceSection?: ApplicationSourceSection;
    withoutLink?: boolean;
    hideCell?: {
        propertyPlan?: boolean;
        propertyNumber?: boolean;
        propertyRooms?: boolean;
        propertyArea?: boolean;
        plotArea?: boolean;
        propertyFloor?: boolean;
        propertyPrice?: boolean;
        propertyGotoLink?: boolean;
    };
    handleDivClick?: (value: number) => void;
    textLinkButton?: boolean;
    icon?: React.ReactNode;
    loaderWrapperBackground?: string;
    gtmMeta: {
        listId: string;
        listName: string;
    };
}

export interface IOfferPropsForPropertyList {
    address: string;
    configuration: {
        display_type: OfferDisplayType;
        is_autoresponder_enabled: boolean;
        limited_presentation?: boolean;
        phone_clip: string | null;
    };
    geo_point: {
        coordinates: [number, number];
    };
    has_required_prices: boolean | null;
    id: number;
    is_active?: boolean;
    is_condohotel?: boolean;
    is_holiday_location?: boolean;
    is_phone_clip_enabled: boolean;
    name: string;
    price_type?: OfferPriceType | null;
    region: {
        country: Country;
        id: number;
        full_name: string;
        stats?: {
            region_type_voivodeship?: {name: string} | null;
            region_type_city?: {name: string} | null;
            region_type_town?: {name: string} | null;
            region_type_district?: {name: string} | null;
        };
    };
    sales_offices: ISalesOffice[];
    slug: string;
    stats: {
        properties_with_roi_count: number;
    } | null;
    type: OfferType;
    vendor: {
        configuration?: {
            privacy_policy_url?: string | null;
            privacy_policy_file?: string | null;
        };
        id: number;
        name: string;
        logo: {
            v_log_80x60: string;
            v_log_120x90: string;
            v_log_160x120: string;
        } | null;
        offices?: {
            address: string;
            phone: string;
            mobile: string;
            type: number;
            region: {
                id: number;
                short_name: string;
            };
        }[];
        slug: string;
    };
}

export const propertiesSortOptions = {
    NUMBER: {value: "number", shortName: "Numer", showOnMobile: false},
    PRICE: {value: "price", shortName: "Cena", showOnMobile: true},
    ROOMS: {value: "rooms", shortName: "Pokoje", showOnMobile: true},
    FLOOR: {value: "floor", shortName: "Piętro", showOnMobile: false},
    PLOT_AREA: {value: "plot_area", shortName: "Pow. działki", showOnMobile: false},
    AREA: {value: "area", shortName: "Metraż", showOnMobile: true}
};

export const PropertyListTable: React.FC<IPropertyListProps> = (props) => {
    const [isModalOpened, setIsModalOpened] = useState(false);
    const [currentProperty, setCurrentProperty] = useState(0);

    const shouldShowPrice = useShouldShowPrice({
        vendorId: props.offer.vendor.id
    });

    const processedProperties = useMemo(() => {
        if (!shouldShowPrice) {
            return props.properties.map((property) => ({
                ...property,
                price: null
            }));
        }

        return props.properties;
    }, [props.properties, shouldShowPrice]);

    const {containerId} = useGTMEcommercePropertyListViewTracking({
        offer: props.offer,
        properties: processedProperties,
        listId: props.gtmMeta.listId,
        listName: props.gtmMeta.listName,
        useVisibilityTracking: true
    });

    useEffect(() => {
        if (!isModalOpened && currentProperty !== 0) {
            setCurrentProperty(0);
        }
    }, [isModalOpened]);

    const openModal = () => setIsModalOpened(true);

    const closeModal = () => setIsModalOpened(false);

    const onSortChange = (value: string) => {
        props.onSortChange(value);
    };

    const onPlanClick = (e: React.MouseEvent<HTMLElement>, index: number) => {
        e.preventDefault();
        setCurrentProperty(index);
        showPropertyPlanView(ShowPropertyPlanView.PROPERTY_LIST, props.offer, props.offer.vendor);
        openModal();
    };

    const onNextClick = async (currentIndex: number) => {
        if (currentIndex === processedProperties.length - 1) {
            setCurrentProperty(0);
            await props.onPageChange(props.currentPage + 1);
        } else {
            setCurrentProperty(currentIndex + 1);
        }
    };

    const onPrevClick = async (currentIndex: number) => {
        if (currentIndex === 0) {
            const results = await props.onPageChange(props.currentPage - 1);
            results && setCurrentProperty(results.length - 1);
        } else {
            setCurrentProperty(currentIndex - 1);
        }
    };

    const buildLink = (propertyId: number) => {
        return rpAppLink.property.detail.base({
            vendorSlug: props.offer.vendor.slug,
            offerSlug: props.offer.slug,
            offerId: props.offer.id,
            propertyId: propertyId
        });
    };

    const isForSaleOverLimit = getOfferDisplayStatus(props.offer) === "limited";
    const isAutoresponderEnabled = props.offer.configuration.is_autoresponder_enabled;
    const showPriceColumn = !isForSaleOverLimit || (isForSaleOverLimit && isAutoresponderEnabled);

    const trackedData = {
        offer: {
            geo_point: {
                coordinates: props.offer.geo_point.coordinates
            },
            id: props.offer.id,
            name: props.offer.name,
            type: props.offer.type,
            region: {
                country: props.offer.region.country,
                full_name: props.offer.region.full_name,
                id: props.offer.region.id
            }
        },
        vendor: {
            id: props.offer.vendor.id,
            name: props.offer.vendor.name,
            slug: props.offer.vendor.slug
        }
    };

    const onPlanButtonClick = () => {
        showPropertyPlanView(ShowPropertyPlanView.PLAN_IMAGE_DOWNLOAD, trackedData.offer, trackedData.vendor);
    };

    if (processedProperties.length > 0) {
        return (
            <div css={tableTrack} id={containerId} data-id={containerId}>
                <div css={table} className={props.className}>
                    <PropertyListTableHeader
                        offer={props.offer}
                        sort={props.sort}
                        onSortChange={onSortChange}
                        showPriceColumn={showPriceColumn}
                        hideCell={props.hideCell}
                    />

                    {processedProperties.map((property, index) => {
                        return (
                            <PropertyListTableRow
                                vendor={props.offer.vendor}
                                key={property.id}
                                offer={props.offer}
                                link={buildLink(property.id)}
                                property={property}
                                sort={props.sort}
                                isDedicatedForRent={property.roi != null && property.roi > 0}
                                onPlanClick={onPlanClick}
                                index={index}
                                sourceSection={props.sourceSection}
                                showPriceColumn={showPriceColumn}
                                withoutLink={props.withoutLink}
                                hideCell={props.hideCell}
                                handleDivClick={props.handleDivClick}
                                textLinkButton={props.textLinkButton}
                                icon={props.icon}
                            />
                        );
                    })}

                    <PropertyPlanModal
                        isOpen={isModalOpened}
                        property={processedProperties[currentProperty]}
                        offer={props.offer}
                        pageCount={props.pageCount}
                        pageIsLoading={props.pageIsLoading}
                        currentPropertyIndex={currentProperty}
                        currentPage={props.currentPage}
                        propertiesOnCurrentPage={processedProperties.length}
                        handleClose={closeModal}
                        handleNextClick={onNextClick}
                        handlePrevClick={onPrevClick}
                        onPlanDownloadClick={onPlanButtonClick}
                    />

                    {props.requestState === RequestState.Waiting && <PropertyListLoader backgroundColor={props.loaderWrapperBackground} useTopOffset />}
                </div>
            </div>
        );
    } else if (props.requestState === RequestState.Waiting) {
        return (
            <div css={loaderWrapNoProperties}>
                <Loader />
            </div>
        );
    } else {
        return <div css={noOffersText}>Brak ofert do wyświetlenia</div>;
    }
};

const table = (props: Theme) => css`
    background-color: #fff;
    display: table;
    width: 100%;
    margin-bottom: 1.6rem;
    position: relative;

    @media (min-width: ${props.breakpoints.md}) {
        margin-bottom: 2.9rem;
        background-color: transparent;
    }
`;

const noOffersText = css`
    font-size: 1.6rem;
    line-height: 2.4rem;
    padding: 1.4rem;
`;

const loaderWrapNoProperties = css`
    max-height: 100%;
    max-width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`;

// tableTrack added for tiny 320px screens like iPhone 5/SE (2016)
// does not allow table to stretch the layout off-screen,  but still enables horizontal scroll
const tableTrack = css`
    max-width: 100vw;
    overflow-x: auto;
`;
import React, {useEffect, useMemo, useState} from "react";
import {css, Theme} from "@emotion/react";

import {Loader} from "@pg-design/loader-module";
import {RequestState} from "@pg-mono/request-state";
import {rpAppLink} from "@pg-mono/rp-routes";

import {ApplicationSourceSection} from "../application/utils/ApplicationSource";
import {useShouldShowPrice} from "../hooks/use_should_show_price";
import {OfferDisplayType} from "../offer/helpers/OfferDisplayType";
import {IMapOfferSidebar} from "../offer/map/types/MapOfferSidebar";
import {IOfferDetail} from "../offer/types/IOfferDetail";
import {ISalesOffice} from "../offer/types/ISalesOffice";
import {OfferPriceType} from "../offer/types/OfferPriceType";
import {OfferType} from "../offer/types/OfferType";
import {getOfferDisplayStatus} from "../offer/utils/get_offer_display_status";
import {PropertyPlanModal} from "../property/components/property_plan_modal/PropertyPlanModal";
import {IPropertyListProperty} from "../property/types/PropertyListProperty";
import {Country} from "../region/types/Country";
import {ShowPropertyPlanView, showPropertyPlanView} from "../tracking/algolytics_hits/click_actions/show_property_plan_click";
import {useGTMEcommercePropertyListViewTracking} from "../tracking/hooks/use_gtm_ecommerce_property_list_view_tracking";
import {PropertyListLoader} from "./PropertyListLoader";
import {PropertyListTableHeader} from "./PropertyListTableHeader";
import {PropertyListTableRow} from "./PropertyListTableRow";

export interface IPropertyListProps {
    offer: IOfferDetail | IMapOfferSidebar;
    properties: IPropertyListProperty[];
    requestState: RequestState;
    sort: string | null;
    onSortChange: (value: string) => void;
    className?: string;
    onPageChange: (value: number) => Promise<unknown[] | void>;
    pageCount: number;
    pageIsLoading: boolean;
    currentPage: number;
    sourceSection?: ApplicationSourceSection;
    withoutLink?: boolean;
    hideCell?: {
        propertyPlan?: boolean;
        propertyNumber?: boolean;
        propertyRooms?: boolean;
        propertyArea?: boolean;
        plotArea?: boolean;
        propertyFloor?: boolean;
        propertyPrice?: boolean;
        propertyGotoLink?: boolean;
    };
    handleDivClick?: (value: number) => void;
    textLinkButton?: boolean;
    icon?: React.ReactNode;
    loaderWrapperBackground?: string;
    gtmMeta: {
        listId: string;
        listName: string;
    };
}

export interface IOfferPropsForPropertyList {
    address: string;
    configuration: {
        display_type: OfferDisplayType;
        is_autoresponder_enabled: boolean;
        limited_presentation?: boolean;
        phone_clip: string | null;
    };
    geo_point: {
        coordinates: [number, number];
    };
    has_required_prices: boolean | null;
    id: number;
    is_active?: boolean;
    is_condohotel?: boolean;
    is_holiday_location?: boolean;
    is_phone_clip_enabled: boolean;
    name: string;
    price_type?: OfferPriceType | null;
    region: {
        country: Country;
        id: number;
        full_name: string;
        stats?: {
            region_type_voivodeship?: {name: string} | null;
            region_type_city?: {name: string} | null;
            region_type_town?: {name: string} | null;
            region_type_district?: {name: string} | null;
        };
    };
    sales_offices: ISalesOffice[];
    slug: string;
    stats: {
        properties_with_roi_count: number;
    } | null;
    type: OfferType;
    vendor: {
        configuration?: {
            privacy_policy_url?: string | null;
            privacy_policy_file?: string | null;
        };
        id: number;
        name: string;
        logo: {
            v_log_80x60: string;
            v_log_120x90: string;
            v_log_160x120: string;
        } | null;
        offices?: {
            address: string;
            phone: string;
            mobile: string;
            type: number;
            region: {
                id: number;
                short_name: string;
            };
        }[];
        slug: string;
    };
}

export const propertiesSortOptions = {
    NUMBER: {value: "number", shortName: "Numer", showOnMobile: false},
    PRICE: {value: "price", shortName: "Cena", showOnMobile: true},
    ROOMS: {value: "rooms", shortName: "Pokoje", showOnMobile: true},
    FLOOR: {value: "floor", shortName: "Piętro", showOnMobile: false},
    PLOT_AREA: {value: "plot_area", shortName: "Pow. działki", showOnMobile: false},
    AREA: {value: "area", shortName: "Metraż", showOnMobile: true}
};

export const PropertyListTable: React.FC<IPropertyListProps> = (props) => {
    const [isModalOpened, setIsModalOpened] = useState(false);
    const [currentProperty, setCurrentProperty] = useState(0);

    const shouldShowPrice = useShouldShowPrice({
        vendorId: props.offer.vendor.id
    });

    const processedProperties = useMemo(() => {
        if (!shouldShowPrice) {
            return props.properties.map((property) => ({
                ...property,
                price: null
            }));
        }

        return props.properties;
    }, [props.properties, shouldShowPrice]);

    const {containerId} = useGTMEcommercePropertyListViewTracking({
        offer: props.offer,
        properties: processedProperties,
        listId: props.gtmMeta.listId,
        listName: props.gtmMeta.listName,
        useVisibilityTracking: true
    });

    useEffect(() => {
        if (!isModalOpened && currentProperty !== 0) {
            setCurrentProperty(0);
        }
    }, [isModalOpened]);

    const openModal = () => setIsModalOpened(true);

    const closeModal = () => setIsModalOpened(false);

    const onSortChange = (value: string) => {
        props.onSortChange(value);
    };

    const onPlanClick = (e: React.MouseEvent<HTMLElement>, index: number) => {
        e.preventDefault();
        setCurrentProperty(index);
        showPropertyPlanView(ShowPropertyPlanView.PROPERTY_LIST, props.offer, props.offer.vendor);
        openModal();
    };

    const onNextClick = async (currentIndex: number) => {
        if (currentIndex === processedProperties.length - 1) {
            setCurrentProperty(0);
            await props.onPageChange(props.currentPage + 1);
        } else {
            setCurrentProperty(currentIndex + 1);
        }
    };

    const onPrevClick = async (currentIndex: number) => {
        if (currentIndex === 0) {
            const results = await props.onPageChange(props.currentPage - 1);
            results && setCurrentProperty(results.length - 1);
        } else {
            setCurrentProperty(currentIndex - 1);
        }
    };

    const buildLink = (propertyId: number) => {
        return rpAppLink.property.detail.base({
            vendorSlug: props.offer.vendor.slug,
            offerSlug: props.offer.slug,
            offerId: props.offer.id,
            propertyId: propertyId
        });
    };

    const isForSaleOverLimit = getOfferDisplayStatus(props.offer) === "limited";
    const isAutoresponderEnabled = props.offer.configuration.is_autoresponder_enabled;
    const showPriceColumn = !isForSaleOverLimit || (isForSaleOverLimit && isAutoresponderEnabled);

    const trackedData = {
        offer: {
            geo_point: {
                coordinates: props.offer.geo_point.coordinates
            },
            id: props.offer.id,
            name: props.offer.name,
            type: props.offer.type,
            region: {
                country: props.offer.region.country,
                full_name: props.offer.region.full_name,
                id: props.offer.region.id
            }
        },
        vendor: {
            id: props.offer.vendor.id,
            name: props.offer.vendor.name,
            slug: props.offer.vendor.slug
        }
    };

    const onPlanButtonClick = () => {
        showPropertyPlanView(ShowPropertyPlanView.PLAN_IMAGE_DOWNLOAD, trackedData.offer, trackedData.vendor);
    };

    if (processedProperties.length > 0) {
        return (
            <div css={tableTrack} id={containerId} data-id={containerId}>
                <div css={table} className={props.className}>
                    <PropertyListTableHeader
                        offer={props.offer}
                        sort={props.sort}
                        onSortChange={onSortChange}
                        showPriceColumn={showPriceColumn}
                        hideCell={props.hideCell}
                    />

                    {processedProperties.map((property, index) => {
                        return (
                            <PropertyListTableRow
                                vendor={props.offer.vendor}
                                key={property.id}
                                offer={props.offer}
                                link={buildLink(property.id)}
                                property={property}
                                sort={props.sort}
                                isDedicatedForRent={property.roi != null && property.roi > 0}
                                onPlanClick={onPlanClick}
                                index={index}
                                sourceSection={props.sourceSection}
                                showPriceColumn={showPriceColumn}
                                withoutLink={props.withoutLink}
                                hideCell={props.hideCell}
                                handleDivClick={props.handleDivClick}
                                textLinkButton={props.textLinkButton}
                                icon={props.icon}
                            />
                        );
                    })}

                    <PropertyPlanModal
                        isOpen={isModalOpened}
                        property={processedProperties[currentProperty]}
                        offer={props.offer}
                        pageCount={props.pageCount}
                        pageIsLoading={props.pageIsLoading}
                        currentPropertyIndex={currentProperty}
                        currentPage={props.currentPage}
                        propertiesOnCurrentPage={processedProperties.length}
                        handleClose={closeModal}
                        handleNextClick={onNextClick}
                        handlePrevClick={onPrevClick}
                        onPlanDownloadClick={onPlanButtonClick}
                    />

                    {props.requestState === RequestState.Waiting && <PropertyListLoader backgroundColor={props.loaderWrapperBackground} useTopOffset />}
                </div>
            </div>
        );
    } else if (props.requestState === RequestState.Waiting) {
        return (
            <div css={loaderWrapNoProperties}>
                <Loader />
            </div>
        );
    } else {
        return <div css={noOffersText}>Brak ofert do wyświetlenia</div>;
    }
};

const table = (props: Theme) => css`
    background-color: #fff;
    display: table;
    width: 100%;
    margin-bottom: 1.6rem;
    position: relative;

    @media (min-width: ${props.breakpoints.md}) {
        margin-bottom: 2.9rem;
        background-color: transparent;
    }
`;

const noOffersText = css`
    font-size: 1.6rem;
    line-height: 2.4rem;
    padding: 1.4rem;
`;

const loaderWrapNoProperties = css`
    max-height: 100%;
    max-width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`;

// tableTrack added for tiny 320px screens like iPhone 5/SE (2016)
// does not allow table to stretch the layout off-screen,  but still enables horizontal scroll
const tableTrack = css`
    max-width: 100vw;
    overflow-x: auto;
`;
